import React, { useState } from "react";

import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";

import styles from "./ResetPasswordTab.module.scss";

interface IProps {
  currentTab: number;
  tabIndex: number;
}

const ResetPasswordTab: React.FC<IProps> = ({ currentTab, tabIndex }) => {
  const [email, setEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  if (currentTab !== tabIndex) return null;

  const passwordsMatch = newPassword === confirmPassword;
  const isValid =
    email.trim().length > 0 &&
    newPassword.length >= 8 &&
    passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    try {
      const result = await apiService.adminResetPassword(email.trim(), newPassword);
      toast.show({
        title: "Reset Password",
        content: result.message,
        duration: 8000,
        type: "success",
      });
      setEmail("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.show({
        title: "Reset Password",
        content: err?.response?.data?.message ?? "Failed to reset password. Check the email and try again.",
        duration: 8000,
        type: "fail",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.ResetPasswordTab}>
      <div className={styles.card}>
        <h2>Reset User Password</h2>
        <p className={styles.description}>
          Enter the user's email address and a new password to immediately reset their credentials. Use this for support when a user is locked out or experiencing login issues.
        </p>

        <form onSubmit={handleSubmit} className={styles.form} autoComplete="off">
          <div className={styles.field}>
            <Input
              label="User Email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className={styles.field}>
            <Input
              label="New Password"
              type="password"
              placeholder="Min. 8 characters"
              value={newPassword}
              onChange={(e: any) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <div className={styles.field}>
            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e: any) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className={styles.error}>Passwords do not match.</p>
            )}
          </div>

          <div className={styles.actions}>
            <Button
              type="submit"
              isLoading={submitting}
              disabled={!isValid || submitting}
            >
              Reset Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordTab;
